"use client";

import { m as motion } from "framer-motion";
import Link from "next/link";
import { useState, type CSSProperties } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDown,
  Check,
  X,
  PhoneCall,
  Clock,
  Wallet,
  Compass,
  ChevronDown,
  Quote,
  Sparkles,
} from "lucide-react";
import type { Service } from "@/content/data/services";
import type { ServiceDetail } from "@/content/data/service-details";
import { ServiceMotif } from "@/components/services/ServiceMotif";
import { PhoneDemo } from "@/components/services/detail/PhoneDemo";
import { trackEvent } from "@/lib/track";

interface RelatedItem {
  slug: string;
  name: string;
  tagline: string;
  category: string;
  href: string;
}

interface Props {
  service: Service;
  detail?: ServiceDetail;
  related: RelatedItem[];
  ctaHref: string;
  ctaLabel: string;
  bookingHref: string;
  allServicesHref: string;
}

// Accento "shine" sul titolo del servizio: parola chiave evidenziata come negli
// altri hero (Dimensione, piano chiaro...). Default: ultima parola del nome;
// override dove la parola chiave non coincide con l'ultima.
// Highlighted words of each H1 (default: last word).
const HERO_ACCENT_OVERRIDE: Record<string, string> = {
  "Siti WordPress su misura per PMI": "WordPress",
  "Velocizzare un sito WordPress e portare i Core Web Vitals in verde": "WordPress",
  "Ecommerce WooCommerce su misura": "WooCommerce",
  "La tua app fatta con Lovable, Bubble o v0 non regge più?": "Lovable, Bubble o v0",
  "Automazioni con l'AI per PMI: il lavoro ripetitivo si fa da solo": "si fa da solo",
  "Esperienze 3D su misura per il web": "3D",
  "Manutenzione evolutiva per siti e app": "evolutiva",
  "App mobile per iPhone e Android": "iPhone e Android",
};

function renderShineTitle(name: string) {
  const accent = HERO_ACCENT_OVERRIDE[name] ?? name.split(" ").pop() ?? "";
  const idx = accent ? name.lastIndexOf(accent) : -1;
  if (idx < 0) return name;
  return (
    <>
      {name.slice(0, idx)}
      <span className="text-shine">{name.slice(idx, idx + accent.length)}</span>
      {name.slice(idx + accent.length)}
    </>
  );
}

const CAT_LABEL: Record<string, string> = {
  wordpress: "WordPress",
  custom: "Sviluppo su misura",
  "ai-rescue": "Da no-code a prodotto",
  automation: "Automazioni",
  creative: "Creatività e 3D",
  retainer: "Manutenzione",
};

const MOTIF_BY_SLUG: Record<string, string> = {
  "bug-fixing-ai-nocode": "rebuild",
  "siti-wordpress": "gauge",
  "wordpress-performance": "gauge",
  "ecommerce-woocommerce": "cart",
  "sviluppo-custom-nextjs": "code",
  "mvp-saas": "code",
  "app-mobile": "phone",
  "automazioni-n8n-ai": "nodes",
  "siti-3d-creative": "cube",
  "manutenzione-evolutiva": "target",
};

function euro(n: number) {
  return n.toLocaleString("it-IT");
}

function deliveryLabel(min: number, max: number) {
  if (min === 0 && max === 0) return "Continuativo";
  if (min === max) return `${min} giorni`;
  return `${min}-${max} giorni`;
}

// Evidenzia in ciano i punti piu' importanti del testo: prezzi e frasi chiave.
const HL_PHRASES = [
  "di tua proprietà",
  "il codice è tuo",
  "codice è tuo",
  "codice resta tuo",
  "codice tuo",
  "un solo referente",
  "un unico referente",
  "prezzo deciso prima",
  "senza sorprese",
  "niente sorprese",
  "senza commissioni",
  "nessun lock-in",
  "nessun vincolo",
  "in locale",
  "gratis",
  "senza limiti",
  "su misura",
  "in autonomia",
  "tempi chiari",
  "iPhone e Android",
  "su Google",
  "prima versione",
  "i tuoi dati al sicuro",
];

function hl(text: string) {
  const price = "(?:€\\s?\\d[\\d.]*|\\d[\\d.]*\\s?(?:euro|€))";
  const phrases = HL_PHRASES.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const re = new RegExp(`(${price}|${phrases})`, "gi");
  const test = new RegExp(`^(?:${price}|${phrases})$`, "i");
  return text.split(re).map((part, i) =>
    part && test.test(part) ? (
      <span key={i} className="font-medium text-primary">
        {part}
      </span>
    ) : (
      part
    )
  );
}

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5 },
};

export function ServiceDetailClient({
  service,
  detail,
  related,
  ctaHref,
  ctaLabel,
  bookingHref,
  allServicesHref,
}: Props) {
  // Accento coerente col brand v3 (ciano). L'identità per-servizio arriva dal
  // motif animato e dalla sezione signature, non dal colore.
  const accent = { ["--svc" as string]: "hsl(var(--primary))" } as CSSProperties;
  const motif = MOTIF_BY_SLUG[service.slug] ?? "code";
  const categoryLabel = CAT_LABEL[service.category] ?? service.category;

  const projectPkgs = service.pricing.packages.filter((p) => !p.unit);
  const headlinePool = projectPkgs.length > 0 ? projectPkgs : service.pricing.packages;
  const headlinePrice =
    service.pricing.publishPrices && headlinePool.length > 0
      ? Math.min(...headlinePool.map((p) => p.priceFrom))
      : null;

  const includedPkg =
    service.pricing.packages.find((p) => p.highlight) ?? projectPkgs[0] ?? service.pricing.packages[0];

  const startChip = service.pricing.discoveryRequired
    ? service.pricing.discoveryPrice
      ? `Diagnosi € ${euro(service.pricing.discoveryPrice)}`
      : "Diagnosi iniziale"
    : "Call gratuita";

  // La CTA principale porta alla call oppure all'abbonamento (defaultCta del
  // servizio): evento coerente con la destinazione.
  const trackPrimaryCta = (location: string) => {
    if (ctaHref === bookingHref) {
      trackEvent("book_call_click", { tool: "tidycal", location });
    } else {
      trackEvent("cta_click", { cta_text: ctaLabel, cta_section: location });
    }
  };

  return (
    <div style={accent}>
      {/* ── HERO ───────────────────────────────────────────────── */}
      <section className="section-padding pt-8 md:pt-12 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div
          className="absolute -top-24 right-0 h-[460px] w-[460px] rounded-full blur-3xl opacity-30"
          style={{ background: "var(--svc)" }}
        />
        <div className="container-wide relative">
          <Link
            href={allServicesHref}
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
          >
            <ArrowRight className="h-4 w-4 rotate-180" />
            Tutti i servizi
          </Link>

          <div className="grid items-center gap-10 xl:grid-cols-[1.25fr_0.75fr]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-mono text-label text-primary mb-4 block">
                Servizio · {categoryLabel}
              </span>
              <h1 className="page-hero-title mb-5">{renderShineTitle(service.h1 ?? service.name)}</h1>
              <p className="text-body-lg text-muted-foreground mb-8 max-w-2xl leading-relaxed">
                {detail?.tagline ?? service.tagline}
              </p>

              <div className="mb-8 flex flex-wrap gap-3">
                <Chip icon={Clock} label="Consegna" value={deliveryLabel(service.deliveryDays.min, service.deliveryDays.max)} />
                {headlinePrice !== null ? (
                  <Chip icon={Wallet} label="Prezzo da" value={`€ ${euro(headlinePrice)}`} highlight />
                ) : (
                  <Chip icon={Wallet} label="Prezzo" value="Su preventivo" />
                )}
                <Chip icon={Compass} label="Per iniziare" value={startChip} />
              </div>

              <div className="flex flex-col gap-4 sm:flex-row">
                <Link
                  href={ctaHref}
                  onClick={() => trackPrimaryCta("service_hero")}
                  className="btn-glow inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-glow px-7 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  {ctaLabel}
                  <PhoneCall className="h-4 w-4" />
                </Link>
                {service.pricing.publishPrices && (
                  <a
                    href="#prezzi"
                    className="rotating-border inline-flex h-12 items-center justify-center gap-2 rounded-xl px-7 font-semibold text-foreground"
                  >
                    Vedi prezzi
                    <ArrowRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </motion.div>

            {detail?.phoneDemo ? (
              <div className="flex justify-center">
                <PhoneDemo {...detail.phoneDemo} />
              </div>
            ) : (
              <div className="hidden xl:flex items-center justify-center">
                <div className="relative h-52 w-52">
                  <div
                    className="pointer-events-none absolute inset-0 rounded-full blur-2xl opacity-25"
                    style={{ background: "var(--svc)" }}
                  />
                  <div className="relative h-full w-full">
                    <ServiceMotif variant={motif} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── IN BREVE ───────────────────────────────────────────── */}
      <section className="section-padding pt-0 lg:pb-24">
        <div className="container-wide">
          <motion.div {...fadeUp} className="surface-card p-6 md:p-8 xl:p-10 max-w-4xl">
            <span className="font-mono text-label text-primary mb-3 block">In breve</span>
            <p className="text-body md:text-body-lg leading-relaxed text-foreground/90">
              {hl(detail?.inBreve ?? service.aeoAnswer)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── ESEMPI PRIMA E DOPO ───────────────────────────────── */}
      {detail?.examples && detail.examples.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="mb-8 max-w-2xl">
              <span className="font-mono text-label text-primary mb-3 block">Esempi concreti</span>
              <h2 className="font-display text-h2 font-bold">Prima e dopo, in pratica</h2>
            </motion.div>
            <div className="grid gap-4 lg:grid-cols-3">
              {detail.examples.map((ex, i) => (
                <motion.div
                  key={ex.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="surface-card flex flex-col p-6 md:p-7"
                >
                  <span className="mb-4 inline-flex w-fit rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[11px] uppercase tracking-[0.06em] text-primary">
                    {ex.tag}
                  </span>
                  <h3 className="mb-4 font-display text-lg font-semibold">{ex.title}</h3>
                  <div className="rounded-xl border border-[hsl(var(--border))] bg-background/40 p-4">
                    <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                      Oggi
                    </p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{ex.before}</p>
                  </div>
                  <ArrowDown className="mx-auto my-2 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <div className="flex-1 rounded-xl border border-primary/30 bg-primary/5 p-4">
                    <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.06em] text-primary">Dopo</p>
                    <p className="text-sm leading-relaxed text-foreground/90">{hl(ex.after)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── INCLUSO / NON INCLUSO ─────────────────────────────── */}
      {(detail?.included?.length || includedPkg) && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="mb-8 max-w-2xl">
              <span className="font-mono text-label text-primary mb-3 block">Cosa ricevi</span>
              <h2 className="font-display text-h2 font-bold xl:whitespace-nowrap">Cosa è incluso, spiegato</h2>
              <p className="mt-3 text-muted-foreground">
                Tutto quello che trovi nel servizio, in chiaro. Così sai esattamente cosa ricevi e
                resti tranquillo, senza sorprese.
              </p>
            </motion.div>

            <motion.div {...fadeUp} className="surface-card p-6 md:p-8 xl:p-10">
              {detail?.included && detail.included.length > 0 ? (
                <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2">
                  {detail.included.map((item) => (
                    <div
                      key={item.label}
                      className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-3"
                    >
                      <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      <div>
                        <h3 className="font-display text-base font-semibold">{item.label}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                          {hl(item.text)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <ul className="grid gap-3 sm:grid-cols-2">
                  {includedPkg?.includes.map((item) => (
                    <li key={item} className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-sm text-foreground/90">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>

            {detail?.notIncluded && detail.notIncluded.length > 0 && (
              <motion.div {...fadeUp} className="surface-card card-hover-red mt-6 p-6 md:p-8">
                <h3 className="mb-5 font-mono text-sm font-semibold uppercase tracking-widest text-[hsl(var(--destructive))]">
                  Cosa non è incluso
                </h3>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {detail.notIncluded.map((item) => (
                    <li key={item} className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--destructive))]" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* ── PER CHI NON È ─────────────────────────────────────── */}
      {detail?.notFor && detail.notFor.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="surface-card max-w-4xl p-6 md:p-8 xl:p-10">
              <span className="font-mono text-label text-primary mb-3 block">Per chi non è</span>
              <h2 className="mb-5 font-display text-2xl font-bold">Te lo dico prima, così non perdiamo tempo</h2>
              <ul className="space-y-3">
                {detail.notFor.map((item) => (
                  <li key={item} className="grid grid-cols-[18px_minmax(0,1fr)] items-start gap-3">
                    <X className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm leading-relaxed text-foreground/90 md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── PROCESSO ──────────────────────────────────────────── */}
      {service.processSteps.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="mb-10 max-w-2xl">
              <span className="font-mono text-label text-primary mb-3 block">Il processo</span>
              <h2 className="font-display text-h2 font-bold xl:whitespace-nowrap">Passo dopo passo, senza sorprese</h2>
            </motion.div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {service.processSteps.map((step, i) => (
                <motion.div
                  key={step.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.45, delay: i * 0.06 }}
                  className="surface-card p-6"
                >
                  <div className="mb-3 flex items-center gap-3">
                    <span
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-mono text-sm font-bold text-primary-foreground"
                      style={{ background: "var(--svc)" }}
                    >
                      {i + 1}
                    </span>
                    <h3 className="font-display text-base font-semibold">{step.name}</h3>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{hl(step.text)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PREZZI ────────────────────────────────────────────── */}
      <section id="prezzi" className="section-padding pt-0 lg:pb-24">
        <div className="container-wide">
          <motion.div {...fadeUp} className="mb-10 max-w-2xl">
            <span className="font-mono text-label text-primary mb-3 block">Prezzi</span>
            <h2 className="font-display text-h2 font-bold xl:whitespace-nowrap">
              {service.pricing.publishPrices ? "Pacchetti chiari, prezzo deciso prima" : "Prezzo su misura"}
            </h2>
            <p className="mt-3 text-muted-foreground">
              {service.pricing.publishPrices
                ? "Cifre indicative di partenza. Il preventivo finale lo definiamo insieme, in chiaro, prima di iniziare."
                : "Per questo servizio il prezzo dipende molto dal progetto. Ne parliamo in una call e ti do una stima onesta."}
            </p>
          </motion.div>

          {service.pricing.publishPrices && service.pricing.packages.length > 0 ? (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {service.pricing.packages.map((pkg) => (
                <motion.div
                  key={pkg.id}
                  {...fadeUp}
                  className={`surface-card flex flex-col p-6 ${
                    pkg.highlight ? "ring-1 ring-primary/40" : ""
                  }`}
                >
                  {pkg.highlight && (
                    <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 font-mono text-[0.65rem] font-medium uppercase tracking-wider text-primary">
                      <Sparkles className="h-3 w-3" /> Consigliato
                    </span>
                  )}
                  <h3 className="font-display text-lg font-semibold">{pkg.label}</h3>
                  <p className="mt-2 mb-1 flex items-baseline gap-1.5">
                    <span className="text-xs text-muted-foreground">
                      {pkg.customQuote ? "indicativo da" : "da"}
                    </span>
                    <span className="font-display text-2xl font-bold text-foreground">
                      € {euro(pkg.priceFrom)}
                    </span>
                    {pkg.unit && <span className="text-sm text-muted-foreground">{pkg.unit}</span>}
                  </p>
                  <p className="mb-4 text-xs text-muted-foreground">
                    {pkg.deliveryDays.min === 0 && pkg.deliveryDays.max === 0
                      ? "Servizio continuativo"
                      : `Consegna ${deliveryLabel(pkg.deliveryDays.min, pkg.deliveryDays.max)}`}
                  </p>
                  <ul className="space-y-2">
                    {pkg.includes.map((item) => (
                      <li key={item} className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                        <span className="text-sm text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div {...fadeUp} className="surface-card p-8 max-w-2xl">
              <p className="text-muted-foreground">
                Raccontami il progetto in una call gratuita: definiamo insieme cosa serve e una stima
                realistica, senza impegno.
              </p>
              <Link
                href={bookingHref}
                onClick={() =>
                  trackEvent("book_call_click", {
                    tool: "tidycal",
                    location: "service_pricing",
                  })
                }
                className="btn-glow mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-glow px-7 font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Prenota una call
                <PhoneCall className="h-4 w-4" />
              </Link>
            </motion.div>
          )}

          {/* Addons */}
          {service.pricing.addons.length > 0 && (
            <motion.div {...fadeUp} className="mt-8">
              <h3 className="mb-4 font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                Moduli aggiuntivi
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {service.pricing.addons.map((addon) => (
                  <div
                    key={addon.label}
                    className="rounded-xl border border-[hsl(var(--border))] bg-surface/40 p-4"
                  >
                    <p className="text-sm text-foreground/90">{addon.label}</p>
                    {/* Copy rule: add-on prices are never shown, they leave room to negotiate. */}
                    <p className="mt-1.5 text-sm font-medium text-primary">Su preventivo</p>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          <motion.p {...fadeUp} className="mt-8 text-xs text-muted-foreground/70">
            Prezzi indicativi di partenza. Hosting, licenze e contenuti, dove servono, sono a parte
            (li trovi nel riquadro qui sopra). Il preventivo finale lo definiamo insieme prima di
            iniziare.
          </motion.p>
        </div>
      </section>

      {/* ── QUANTO COSTA ALTROVE ──────────────────────────────── */}
      {detail?.marketComparison && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="mb-8 max-w-4xl">
              <span className="font-mono text-label text-primary mb-3 block">Quanto costa altrove</span>
              <h2 className="font-display text-h2 font-bold xl:whitespace-nowrap">Lo stesso risultato, prezzi molto diversi</h2>
              <p className="mt-3 max-w-4xl text-muted-foreground">{detail.marketComparison.intro}</p>
            </motion.div>

            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {detail.marketComparison.columns.map((col, i) => {
                const isUs = i === detail.marketComparison.columns.length - 1;
                return (
                  <motion.div
                    key={col.who}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.45, delay: i * 0.07 }}
                    className={`surface-card flex flex-col p-6 ${isUs ? "ring-1 ring-primary/50" : ""}`}
                    style={isUs ? { borderColor: "var(--svc)" } : undefined}
                  >
                    <h3 className={`font-display text-lg font-semibold ${isUs ? "text-primary" : ""}`}>
                      {col.who}
                    </h3>
                    <p className="mt-2 mb-3 text-sm font-medium text-foreground/90">{col.priceRange}</p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{col.approach}</p>
                  </motion.div>
                );
              })}
            </div>

            <motion.div {...fadeUp} className="mt-6 surface-card p-6 md:p-7">
              <p className="text-body leading-relaxed text-foreground/90">
                <span className="font-semibold text-primary">La differenza in una frase: </span>
                {hl(detail.marketComparison.takeaway)}
              </p>
            </motion.div>

            {detail.marketComparison.sources.length > 0 && (
              <details className="mt-4 text-xs text-muted-foreground/70">
                <summary className="cursor-pointer select-none hover:text-muted-foreground">
                  Fonti dei prezzi di mercato (indicativi)
                </summary>
                <ul className="mt-2 space-y-1 break-all">
                  {detail.marketComparison.sources.map((src) => (
                    <li key={src}>
                      <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="hover:text-primary"
                      >
                        {src}
                      </a>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        </section>
      )}

      {/* ── FAQ ───────────────────────────────────────────────── */}
      {detail?.faq && detail.faq.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="mb-8 max-w-2xl">
              <span className="font-mono text-label text-primary mb-3 block">Domande frequenti</span>
              <h2 className="font-display text-h2 font-bold xl:whitespace-nowrap">I dubbi più comuni, spiegati semplice</h2>
            </motion.div>
            {/* Allineate a sinistra su tutti i breakpoint, sotto al titolo. */}
            <div className="max-w-2xl space-y-3">
              {detail.faq.map((item) => (
                <FaqRow key={item.q} item={item} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── OBIEZIONI ─────────────────────────────────────────── */}
      {service.usualObjections.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div {...fadeUp} className="surface-card p-6 md:p-8 xl:p-10">
              <div className="grid gap-8 xl:grid-cols-[0.9fr_1.1fr] xl:items-center">
                <div>
                  <span className="font-mono text-label text-primary mb-3 block">Onestà</span>
                  <h2 className="font-display text-2xl md:text-3xl font-bold">
                    Le obiezioni che sento più spesso
                  </h2>
                  <p className="mt-3 text-muted-foreground">
                    Le metto sul tavolo io. Se una di queste è il tuo dubbio, ne parliamo in chiaro in
                    call.
                  </p>
                </div>
                <ul className="space-y-3">
                  {service.usualObjections.map((obj) => (
                    <li
                      key={obj}
                      className="flex items-start gap-3 rounded-xl border border-[hsl(var(--border))] bg-surface/40 p-4"
                    >
                      <Quote className="mt-0.5 h-4 w-4 shrink-0 text-primary/70" />
                      <span className="text-sm text-foreground/90">{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── SIGNATURE ─────────────────────────────────────────── */}
      {detail?.signature && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.div
              {...fadeUp}
              className="relative overflow-hidden rounded-2xl border p-8 md:p-12"
              style={{ borderColor: "var(--svc)", background: "hsl(var(--surface))" }}
            >
              <div
                className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl opacity-20"
                style={{ background: "var(--svc)" }}
              />
              <div className="relative">
                <span
                  className="mb-4 inline-block font-mono text-label"
                  style={{ color: "var(--svc)" }}
                >
                  {detail.signature.eyebrow}
                </span>
                <h2 className="font-display text-2xl md:text-4xl font-bold leading-tight max-w-4xl">
                  {detail.signature.title}
                </h2>
                <p className="mt-5 text-body md:text-body-lg leading-relaxed text-muted-foreground">
                  {hl(detail.signature.body)}
                </p>
                <div className="mt-8 grid gap-5 md:grid-cols-3">
                  {detail.signature.points.map((pt) => (
                    <div key={pt.label} className="rounded-xl bg-background/40 p-5">
                      <div className="mb-2 flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ background: "var(--svc)" }}
                        />
                        <h3 className="font-display text-base font-semibold">{pt.label}</h3>
                      </div>
                      <p className="text-sm leading-relaxed text-muted-foreground">{hl(pt.text)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* ── CORRELATI ─────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="section-padding pt-0 lg:pb-24">
          <div className="container-wide">
            <motion.h2 {...fadeUp} className="mb-6 font-display text-2xl md:text-3xl font-bold">
              Servizi correlati
            </motion.h2>
            <div className="grid gap-4 md:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  href={r.href}
                  className="surface-card group p-5 transition-colors hover:border-primary/40"
                >
                  <p className="text-xs font-mono uppercase tracking-wider text-primary mb-2">
                    {CAT_LABEL[r.category] ?? r.category}
                  </p>
                  <h3 className="font-display text-lg font-semibold mb-1 flex items-center gap-1.5">
                    {r.name}
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </h3>
                  <p className="text-sm text-muted-foreground">{r.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA FINALE ────────────────────────────────────────── */}
      <section className="section-padding pb-24 md:pb-32">
        <div className="container-wide">
          <motion.div
            {...fadeUp}
            className="surface-card relative max-w-4xl overflow-hidden p-8 md:p-12 text-left"
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{ background: "var(--svc)" }}
            />
            <div className="relative max-w-2xl">
              <h2 className="font-display text-2xl md:text-4xl font-bold">
                Pronto a partire con {service.name}?
              </h2>
              <p className="mt-4 text-muted-foreground">
                30 minuti in call per capire se ha senso lavorare insieme. Nessun impegno, solo
                chiarezza su tempi e prezzi.
              </p>
              <div className="mt-7 flex flex-col gap-4 sm:flex-row">
                <Link
                  href={ctaHref}
                  onClick={() => trackPrimaryCta("service_cta_final")}
                  className="btn-glow inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-gradient-to-br from-primary to-primary-glow px-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:w-auto sm:px-7 sm:text-base"
                >
                  {ctaLabel}
                  <PhoneCall className="h-4 w-4 shrink-0" />
                </Link>
                <Link
                  href={allServicesHref}
                  className="rotating-border inline-flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-semibold text-foreground sm:w-auto sm:px-7 sm:text-base"
                >
                  Tutti i servizi
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Chip({
  icon: Icon,
  label,
  value,
  highlight,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`inline-flex items-center gap-2.5 rounded-xl border px-4 py-2.5 ${
        highlight
          ? "border-primary/40 bg-primary/5"
          : "border-[hsl(var(--border))] bg-surface/40"
      }`}
    >
      <Icon className={`h-4 w-4 ${highlight ? "text-primary" : "text-muted-foreground"}`} />
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}

function FaqRow({ item, defaultOpen }: { item: { q: string; a: string }; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className="surface-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display text-base font-semibold">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-primary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden"
      >
        <p className="px-5 pb-5 text-sm leading-relaxed text-muted-foreground">{hl(item.a)}</p>
      </motion.div>
    </div>
  );
}
